import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormGroup, UntypedFormGroup } from "@angular/forms";
import { FormService } from "../../services/form.service";
import { MatDialog } from "@angular/material/dialog";
import { TrackingService } from "../../services/tracking.service";

@Component( {
	selector: 'app-consent-movers-list',
	templateUrl: './consent-movers-list.component.html',
	styleUrls: ['./consent-movers-list.component.scss']
} )
export class ConsentMoversListComponent {

	@Input() movers!: FormArray<FormGroup>;
	@Input() viewMode: 'default' | 'full' = 'default';
	@Output() openModal = new EventEmitter<void>();
	@Input() form!: FormGroup
	@Input() displayedIndices: number[] = [];

	consentMovers !: UntypedFormGroup
	formGroupName : string = 'consentMovers'
	readonly maxSelections: number = 5
	private hasTrackedInteraction: boolean = false;
	private hasTrackedModalOpen: boolean = false;

	constructor( 
		public formService: FormService,
		private trackingService: TrackingService
	) {

	}

	public ngOnInit(): void {
		this.setupSelectionLimit();
	}

	private setupSelectionLimit(): void {
		this.movers.controls.forEach(control => {
			control.get('checkbox')?.valueChanges.subscribe(checked => {
				if( checked && this.getSelectedCount() > this.maxSelections ) {
					control.get( 'checkbox' )?.setValue( false, { emitEvent: false } );
				}
			} );
		} );
	}

	public onCheckboxChange(event: Event, control: FormGroup): void {

		const checkbox = event.target as HTMLInputElement;
		const selected = checkbox.checked;
		const moverName = control.get('_name')?.value ?? '';
		const selectedCount = this.getSelectedCount();
		const location = this.viewMode === 'default' ? 'default' : 'modal';

		this.formService.trackEventWithContext( 'Prematch - Mover toggle', 'consentMovers', {
			location,
			mover_name: moverName,
			selected,
			selected_count: selectedCount,
			verified: !! control.get( '_verified' )?.value
		} );
		this.trackingService.clarityEvent( 'prematch_checkbox_interact' );

		// GTM: Track first interaction only when not in modal
		if( this.viewMode === 'default' && ! this.hasTrackedInteraction ) {

			this.trackingService.sendGTMEvent( {
				event: 'mover_checkbox_interaction',
				event_category: 'form_interaction',
				event_action: 'checkbox_click',
				event_label: moverName,
				selected
			} );
			this.hasTrackedInteraction = true;
		}
	}

	// Desktop only: a click anywhere on the card toggles its checkbox. On mobile the
	// card body is inert so a tap while scrolling can't select a mover - only the
	// checkbox's own label is a tap target there. The toggle goes through a real
	// input.click(), so the (change) handler, tracking, the selection cap and the
	// disabled state all behave exactly as a direct checkbox click.
	public onCardClick( event: MouseEvent, checkbox: HTMLInputElement ): void {

		// Clicks on the checkbox or its label already toggle natively - including
		// the synthetic click the label forwards to the input, which bubbles here too
		if( ( event.target as HTMLElement ).closest( '.mover-actions' ) ) {

			return;
		}

		if( this.isMobileViewport() ) {

			return;
		}

		checkbox.click();
	}

	// Same breakpoint as the mobile-query SCSS mixin
	// ($mobile-max-width / $mobile-max-height in src/sass/imports/_variables.scss)
	private isMobileViewport(): boolean {

		return window.matchMedia( '(max-width: 1200px), (max-height: 650px)' ).matches;
	}

	public getSelectedCount(): number {
		return this.movers.controls.filter(control => control.get('checkbox')?.value).length;
	}

	// Whether any card this list renders is verified - gates the compact two-column
	// layout, so lists without a verified mover keep the full-size cards
	public get hasVerifiedMovers(): boolean {

		return this.movers.controls.some( ( control, i ) =>
			( this.viewMode === 'full' || this.displayedIndices.includes( i ) ) && !! control.get( '_verified' )?.value
		);
	}

	public canSelect(control: FormGroup): boolean {
		return control.get('checkbox')?.value || this.getSelectedCount() < this.maxSelections;
	}

	public triggerModal() {

		this.formService.trackEventWithContext( 'Prematch - Open modal', 'consentMovers', {
			selected_count: this.getSelectedCount()
		} );

		this.trackingService.clarityEvent( 'prematch_view_more_movers' );


		// GTM: Track modal open only if not already tracked
		if( ! this.hasTrackedModalOpen ) {

			this.trackingService.sendGTMEvent( {
				event: 'mover_modal_open',
				event_category: 'form_interaction',
				event_action: 'open_modal',
				event_label: 'View other moving companies'
			} );
			this.hasTrackedModalOpen = true;
		}

		this.openModal.emit();
	}

	public getStars( rating: number ) {

		rating = rating / 2;
		const stars: string[] = [];
		const fullStars = Math.floor(rating);
		const hasHalfStar = rating % 1 !== 0;
		const totalStars = 5;

		for (let i = 0; i < fullStars; i++) {
			stars.push('assets/images/stars/star-full.png');
		}

		if (hasHalfStar) {
			stars.push('assets/images/stars/star-half.png');
		}

		while (stars.length < totalStars) {
			stars.push('assets/images/stars/star-empty.png');
		}

		return stars;
	}
}
